import type { ComputedRef, Ref } from 'vue'
import { PlayerAccountClient } from '@core/types'
import { CONTACT_VERIFICATION_STATUS_KEY, computed, OpenapiError, useCookie, useDayjs, useIntervalFn, usePaymentsConfig, usePlayerAccountClient, useQueryClient, useRefetchThrottle, useState, useTimestamp, useTracking, useI18n, useUserInfo } from '#imports'

export type EmailVerificationSendError = 'tooMany' | 'failed'

export type EmailVerificationResult = 'verified' | 'invalidLink' | 'alreadyUsed' | 'failed'

export interface EmailVerificationSendContext {
    surface: 'sheet' | 'panel'
    /** The sheet step or panel state the send came from. */
    step: 'pitch' | 'warning' | 'sent' | 'invalidLink' | 'unsent'
}

export interface UseEmailVerificationReturn {
    /** Asks once per device, until the user declines. */
    shouldShowPrompt: ComputedRef<boolean>
    /** The standing offer once the prompt is declined. */
    shouldShowPanel: ComputedRef<boolean>
    email: ComputedRef<string | null>
    /** The status says the address is verified, also when another tab used the link. */
    verified: ComputedRef<boolean>
    /** A link was sent during this visit. */
    sent: Readonly<Ref<boolean>>
    /** Outcome of a mailed link, kept until the withdraw sheet shows it. */
    result: Ref<EmailVerificationResult | null>
    /** Time left until another link may be asked for, as m:ss; null when one may. */
    sendCooldown: ComputedRef<string | null>
    /** A send button's label: the action, or the time left while links are held back. */
    sendLabel: (action: 'verifyAction' | 'newLinkAction' | 'resendAction') => string
    /** Mails a verification link. Resolves with the reason when none was sent. */
    send: (context: EmailVerificationSendContext) => Promise<EmailVerificationSendError | null>
    /** Confirms the token from a mailed link and records the outcome in `result`. */
    confirm: (token: string) => Promise<void>
    dismiss: () => void
    /** Hides the panel for this visit only. */
    hidePanel: () => void
    /** Sends an `email_verification_*` tracking event. */
    track: (event: EmailVerificationEvent, meta?: Record<string, string | boolean>) => void
}

const STATE = 'payments:emailVerification'

const EVENT_TYPES = {
    prompt_shown: 'Load',
    defer: 'Click',
    closed: 'Click',
    send: 'Click',
    sent: 'Load',
    send_failed: 'Error',
    panel_shown: 'Load',
    panel_closed: 'Click',
    support_click: 'Click',
    link_opened: 'Load',
    link_result: 'Load',
} as const

export type EmailVerificationEvent = keyof typeof EVENT_TYPES

// Gives the mail time to arrive before the user can ask again
const SEND_COOLDOWN_MS = 60 * 1000

// The backend's shortest window (3 links per 10 minutes); its refusal carries no retry time
const TOO_MANY_COOLDOWN_MS = 10 * 60 * 1000

const INVALID_LINK_CODES: string[] = [
    PlayerAccountClient.PublicErrorCodes.PA_1200,
    PlayerAccountClient.PublicErrorCodes.PA_1201,
    PlayerAccountClient.PublicErrorCodes.PA_1206,
]

// The backend rejects a non-UUID token with a plain validation error, so a truncated or mangled link is caught here
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function errorCode(error: unknown): string | undefined {
    return OpenapiError.isError(error) ? error.data?.code : undefined
}

export const useEmailVerification = (): UseEmailVerificationReturn => {
    const { withdraw } = usePaymentsConfig()
    const { session_expiry, useContactVerificationStatus, useRequestContactVerification, useVerifyContact } = usePlayerAccountClient()
    const { data: user } = useUserInfo()
    const queryClient = useQueryClient()
    const dayjs = useDayjs()
    const { trackEvent } = useTracking()
    const { t } = useI18n()

    const status = useContactVerificationStatus({ enabled: !!withdraw?.useEmailVerification && !!session_expiry.value })
    const request = useRequestContactVerification()
    const verify = useVerifyContact()

    const dismissedCookie = useCookie<string | undefined>('emailVerificationPrompt', {
        maxAge: 60 * 60 * 24 * 365,
        sameSite: 'lax',
        secure: true,
        path: '/',
    })

    // Shared state, as the prompt and the panel each call this and useCookie gives each call its own ref
    const dismissed = useState(`${STATE}:dismissed`, () => !!dismissedCookie.value)
    const panelHidden = useState(`${STATE}:panelHidden`, () => false)
    const sent = useState(`${STATE}:sent`, () => false)
    const result = useState<EmailVerificationResult | null>(`${STATE}:result`, () => null)
    // A cookie, so a reload or the mail link's new tab keeps the wait
    const nextSendCookie = useCookie<number | undefined>('emailVerificationNextSend', {
        maxAge: TOO_MANY_COOLDOWN_MS / 1000,
        sameSite: 'lax',
        secure: true,
        path: '/',
    })
    const nextSendAt = useState(`${STATE}:nextSendAt`, () => nextSendCookie.value ?? 0)

    const now = useTimestamp({ scheduler: tick => useIntervalFn(tick, 1000) })
    const sendCooldown = computed(() => {
        const seconds = Math.ceil((nextSendAt.value - now.value) / 1000)

        if (seconds <= 0) return null

        return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
    })

    function sendLabel(action: 'verifyAction' | 'newLinkAction' | 'resendAction') {
        return sendCooldown.value
            ? t('payments.emailVerification.resendIn', { time: sendCooldown.value })
            : t(`payments.emailVerification.${action}`)
    }

    // No status (logged out, loading or failed) shows nothing
    const isUnverified = computed(() => status.data.value?.is_verified === false)
    const verified = computed(() => status.data.value?.is_verified === true)

    const shouldShowPrompt = computed(() => isUnverified.value && !dismissed.value)
    const shouldShowPanel = computed(() => isUnverified.value && dismissed.value && !panelHidden.value)
    // The link goes to the account's current address; the status only knows one once a link was requested
    const email = computed(() => user.value?.email ?? null)

    // The link may have been used in another tab or on another device meanwhile
    useRefetchThrottle({ refetchInput: refreshUnverified, key: STATE, delay: 0, useFocus: true })

    async function refreshUnverified() {
        if (isUnverified.value) await status.refresh()
    }

    function track(event: EmailVerificationEvent, meta: Record<string, string | boolean> = {}) {
        void trackEvent({
            event_name: `email_verification_${event}`,
            event_type: EVENT_TYPES[event],
            meta_data: [{ category: 'EmailVerification', ...meta }],
        })
    }

    async function send(context: EmailVerificationSendContext): Promise<EmailVerificationSendError | null> {
        if (nextSendAt.value > dayjs().valueOf()) return 'tooMany'

        const meta = { ...context, kind: context.step === 'sent' || context.step === 'invalidLink' ? 'resend' : 'first' }

        track('send', meta)

        // A new link marks the address unverified again, so never send one for a verified address
        await refreshUnverified()

        if (verified.value) {
            track('send_failed', { ...meta, reason: 'alreadyVerified' })

            return null
        }

        try {
            await request.mutateAsync(undefined)
            sent.value = true
            holdSends(SEND_COOLDOWN_MS)
            track('sent', meta)

            return null
        }
        catch (error) {
            const reason = errorCode(error) === PlayerAccountClient.PublicErrorCodes.PA_1205 ? 'tooMany' : 'failed'

            if (reason === 'tooMany') holdSends(TOO_MANY_COOLDOWN_MS)
            track('send_failed', { ...meta, reason })

            return reason
        }
    }

    function holdSends(ms: number) {
        nextSendAt.value = dayjs().valueOf() + ms
        nextSendCookie.value = nextSendAt.value
    }

    async function confirm(token: string) {
        const wellFormed = UUID_PATTERN.test(token)

        track('link_opened', { loggedIn: !!session_expiry.value, tokenFormat: wellFormed ? 'valid' : 'broken' })

        if (!wellFormed) {
            result.value = 'invalidLink'
        }
        else {
            try {
                await verify.mutateAsync(token)
                result.value = 'verified'
                await queryClient.invalidateQueries({ key: CONTACT_VERIFICATION_STATUS_KEY })
            }
            catch (error) {
                const code = errorCode(error)

                if (code && INVALID_LINK_CODES.includes(code)) result.value = 'invalidLink'
                else if (code === PlayerAccountClient.PublicErrorCodes.PA_1202) result.value = 'alreadyUsed'
                else result.value = 'failed'
            }
        }

        track('link_result', { result: result.value })
    }

    function dismiss() {
        dismissedCookie.value = '1'
        dismissed.value = true
    }

    function hidePanel() {
        panelHidden.value = true
    }

    return { shouldShowPrompt, shouldShowPanel, email, verified, sent, result, sendCooldown, sendLabel, send, confirm, dismiss, hidePanel, track }
}
